import { FormattedMessage } from 'react-intl';
import { cx } from '@emotion/css';

import { useStyles } from './contact-us.styles';
import { Icon, IconSizes, IconTypes } from '../../../../../common';
import { ContactUsItem, ContactUsProps } from './contact-us.models';
import {
  PHONE_SUPPORT_HREF,
  EMAIL_HREF,
  PHONE_SUPPORT_PARENTHESES,
} from '../../../../../../constants/common';

const getContactLinks = (onCallBackClick?: () => void): ContactUsItem[] => [
  {
    title: PHONE_SUPPORT_PARENTHESES,
    text: 'common.callSupport.text',
    icon: IconTypes.phoneRetro,
    iconSize: IconSizes.xl,
    href: PHONE_SUPPORT_HREF,
    isPhone: true,
    testId: 'contact-support',
  },
  {
    title: 'common.callBack',
    text: 'common.callBack.text',
    icon: IconTypes.callBack,
    iconSize: IconSizes.xl,
    handleClick: onCallBackClick,
    testId: 'call-back',
  },
  {
    title: 'common.email',
    text: 'common.email.text',
    icon: IconTypes.emailRoundedBlue,
    iconSize: IconSizes.xl,
    href: EMAIL_HREF,
    testId: 'email',
    target: '_blank',
  },
];

const ItemInner = ({ icon, iconSize, text, title, isPhone }: ContactUsItem) => {
  const classes = useStyles();

  return (
    <>
      <span
        aria-hidden="true"
        className={classes.iconWrapper}
        data-testid="icon-wrapper"
      >
        <Icon
          type={icon}
          size={iconSize}
        />
      </span>
      <p data-testid="contact-us-item-text">
        <strong data-testid="contact-us-item-strong">
          {isPhone ? title : <FormattedMessage id={title} />}
        </strong>
        <span className={classes.listNote} data-testid="contact-us-item-regular">
          <FormattedMessage id={text} />
        </span>
      </p>
    </>
  );
};

const ActionItem = ({ icon, iconSize, text, testId, handleClick, title, href, target, isPhone }: ContactUsItem): JSX.Element => {
  const classes = useStyles();
  return handleClick ? (
    <button
      data-testid={testId}
      className={cx(classes.action, classes.callbackAction)}
      type="button"
      onClick={handleClick}
    >
      <ItemInner
        icon={icon}
        iconSize={iconSize}
        text={text}
        title={title}
        isPhone={isPhone}
      />
    </button>
  ) : (
    <a data-testid={testId} className={classes.action} href={href} target={target} rel="noopener noreferrer">
      <ItemInner
        icon={icon}
        iconSize={iconSize}
        text={text}
        title={title}
        isPhone={isPhone}
      />
    </a>
  );
};

export const ContactUs = ({ onCallBackClick }: ContactUsProps): JSX.Element => {
  const classes = useStyles();
  const contactLinks = getContactLinks(onCallBackClick ?? (() => {}));

  return (
    <ul
      className={classes.root}
      data-testid="contact-us-list"
    >
      {contactLinks.map(({ icon, iconSize, title, text, href, isPhone, handleClick, testId, target }, index) => (
        <li data-testid="contact-us-list-item" key={`contact-item-${index}`}>
          <ActionItem
            icon={icon}
            iconSize={iconSize}
            title={title}
            text={text}
            href={href}
            isPhone={isPhone}
            handleClick={handleClick}
            testId={testId}
            target={target}
          />
        </li>
      ))}
    </ul>
  );
};
